import { formatRelative } from "date-fns";

import { selectOutcomeStatusLine } from "./outcome-context";

import {
  OutcomeType,
  PageCategory,
  PageOutcomeType,
  PageType,
  SectionOutcomeType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { RootState } from "store/index";
import {
  activitySelectors,
  outcomeSelectors,
  pageOutcomeSelectors,
  pageSelectors,
  sectionOutcomeSelectors,
  topicSelectors,
} from "store/selectors";

// TODO: This should be adapted by page interaction mode, or explain interaction modes.
export const PAGE_EXPLANATION_CONTEXT = t("ai.page_explanation");

export const pageCategoryToExplanationContext: Record<PageCategory, string> = {
  activity_page: t("ai.topic_explanation"),
  course_resource: t("ai.course_resource_explanation"),
  instructor_workspace: t("ai.instructor_workspace_explanation"),
  assignment: t("ai.assignment_explanation"),
};

export const getPageCategoryContext = (page: PageType | null) => {
  return page ? pageCategoryToExplanationContext[page.category] : null;
};

export const selectPageStatusLine = (state: RootState, page: PageType) => {
  const outcomes = outcomeSelectors.selectAll(state);
  const sectionOutcomes = sectionOutcomeSelectors.selectAll(state);
  const pageOutcomes = pageOutcomeSelectors.selectAll(state);
  return getPageStatusLine(page, pageOutcomes, sectionOutcomes, outcomes);
};

export const getPageStatusLine = (
  page: PageType,
  pageOutcomes: PageOutcomeType[],
  sectionOutcomes: SectionOutcomeType[],
  outcomes: OutcomeType[],
) => {
  const thisPageOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);
  const thisSectionOutcomes = sectionOutcomes.filter((co) =>
    thisPageOutcomes.some((po) => po.section_outcome_id === co.id),
  );
  const thisOutcomes = outcomes.filter((o) =>
    thisSectionOutcomes.some((po) => po.outcome_id === o.id),
  );
  const outcomeStatusLine = selectOutcomeStatusLine(thisOutcomes);
  const status = page.released_at ? t("ai.page_status.released") : t("ai.page_status.unreleased");
  return `Page "${page.title}" (id "${page.id}") (${status}) using ${outcomeStatusLine}`;
};

export const selectPageMetadataContext = (state: RootState, pageId: string) => {
  const page = pageSelectors.selectById(state, pageId);
  if (!page) {
    return "";
  }
  const activity = activitySelectors.selectById(state, page.activity_id);
  const topic = topicSelectors.selectById(state, activity?.topic_id);

  return [
    `The currently selected Page (id "${page.id}") has the following attributes:`,
    `- Title: "${page.title}"`,
    page.category === "activity_page" ? `- Activity Title: "${activity?.title}"` : "",
    page.category === "activity_page" ? `- Topic Title: "${topic?.title}"` : "",
    `- Release status: "${page.released_at ? "Released to students" : "Not released to students"}`,
    `- Due date: "${page.due_at ? formatRelative(page.due_at, new Date()) : "No due date"}`,
    `- Max points: "${page.max_points}"`,
    `- Interaction mode: "${page.interaction_mode}"`,
    page.locked_at
      ? `- Locked for students since: "${formatRelative(page.locked_at, new Date())}"`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
};
